/**
 * Structural Alert Scanner — runs the SMARTS toxicophore filter against
 * compound SMILES using client-side RDKit WASM.
 *
 * This is the implementation layer behind the `scan-structural-alerts`
 * action registered in `registry/actions/medchem-design.js`.
 *
 * Usage pattern:
 *   const matches = await scanSmilesForAlerts(smiles);
 *   // [{ ruleId, alert, mechanism, mitigation, references, smarts }, ...]
 *
 * @module gibbs/knowledge/structuralAlertScanner
 */

import { TOXICOPHORE_RULES } from './medchemRules';
import { loadRDKit } from './rdkitLoader';

/**
 * Scan a single SMILES string for all toxicophore alerts.
 *
 * @param {string} smiles - Compound SMILES
 * @returns {Promise<Array<object>>} matched alerts (copies of the rule
 *   objects; empty array if clean or if RDKit is unavailable)
 */
export async function scanSmilesForAlerts(smiles) {
    if (!smiles || typeof smiles !== 'string') return [];

    let RDKit;
    try {
        RDKit = await loadRDKit();
    } catch (err) {
        // RDKit couldn't load (e.g., tests running in Node without WASM).
        // Degrade gracefully — return empty instead of throwing.
        // eslint-disable-next-line no-console
        console.warn('[StructuralAlertScanner] RDKit unavailable:', err?.message);
        return [];
    }
    if (!RDKit.isInitialized || !RDKit.isInitialized()) {
        // WASM hasn't finished loading yet; caller should retry later.
        return [];
    }

    let mol = null;
    try {
        mol = RDKit.getMol(smiles);
        if (!mol || !mol.is_valid()) return [];
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[StructuralAlertScanner] invalid SMILES:', smiles, err?.message);
        if (mol && mol.delete) mol.delete();
        return [];
    }

    const matches = [];
    for (const rule of TOXICOPHORE_RULES) {
        if (!rule.smarts) continue;
        let qmol = null;
        try {
            qmol = RDKit.getQMol(rule.smarts);
            const matchStr = mol.get_substruct_match(qmol);
            // RDKit returns '{}' for no match, or JSON describing the hit atoms
            const parsed = matchStr && matchStr !== '{}' ? JSON.parse(matchStr) : null;
            if (parsed && parsed.atoms && parsed.atoms.length > 0) {
                matches.push({
                    ruleId: rule.id,
                    alert: rule.alert,
                    mechanism: rule.mechanism,
                    mitigation: rule.mitigation,
                    references: rule.references,
                    smarts: rule.smarts,
                    matchedAtoms: parsed.atoms,
                });
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn(
                `[StructuralAlertScanner] SMARTS match failed for ${rule.id}:`,
                err?.message
            );
        } finally {
            if (qmol && qmol.delete) qmol.delete();
        }
    }

    if (mol && mol.delete) mol.delete();
    return matches;
}

/**
 * Scan a list of compounds (each with a SMILES field) for alerts.
 *
 * @param {Array<{smiles: string, name?: string}>} compounds
 * @returns {Promise<Array<{compound: object, alerts: Array<object>}>>}
 */
export async function scanCompoundsForAlerts(compounds) {
    if (!compounds || compounds.length === 0) return [];
    return Promise.all(
        compounds.map(async (cmp) => ({
            compound: cmp,
            alerts: await scanSmilesForAlerts(cmp.smiles),
        })),
    );
}

/**
 * Render scan results as a chat message. Used by the ChatBot shortcut
 * and by the Workflow Checker when the action completes.
 *
 * @param {Array<{compound: object, alerts: Array<object>}>} results
 * @returns {string} markdown summary
 */
export function formatAlertSummary(results) {
    if (!results || results.length === 0) {
        return 'No compounds to scan.';
    }

    const clean = results.filter((r) => r.alerts.length === 0);
    const flagged = results.filter((r) => r.alerts.length > 0);

    const lines = ['## Structural Alert Scan'];
    lines.push(`- **${clean.length}** compound(s) clean`);
    lines.push(`- **${flagged.length}** compound(s) with alerts`);
    lines.push('');

    for (const { compound, alerts } of flagged) {
        const label = compound.name || compound.smiles || '(unnamed)';
        lines.push(`### ${label}`);
        for (const a of alerts) {
            lines.push(`- **${a.alert}** — ${a.mechanism}`);
            if (a.mitigation) {
                lines.push(`  *Mitigation:* ${a.mitigation}`);
            }
        }
        lines.push('');
    }

    if (flagged.length > 0) {
        lines.push('*Rule source: Meanwell 2023; Stepan & Kalgutkar, Chem. Res. Toxicol. 2011, 24, 1345-1410.*');
    }

    return lines.join('\n');
}

export default {
    scanSmilesForAlerts,
    scanCompoundsForAlerts,
    formatAlertSummary,
};
